import {
  Module,
  Mutation,
  Action,
} from "vuex-class-modules"
import { store } from "../store"
import { Modules } from "../modules"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { ServerResponse } from "@/models/ServerResponse"
import { BaseModule } from "@/models/BaseModule"
import { FilterNotification } from "@/models/Filter"
import { Notification, NotificationIdeaActivity, NotificationInit, NotificationResponse } from "@/models/Notification"
import { parsingServerResponseContent } from "@/utils/helpers"
import { userModule } from "./user.module"
import { AlertContentItem, AlertReport, AlertType } from "@/models"
import { AlertActions, alertModule } from "@/store/modules/alert.module"


export enum NotificationActions {
  GET_NOTIFICATIONS = "GET_NOTIFICATIONS",
  GET_NOTIFICATIONS_RESPONSE = "GET_NOTIFICATIONS_RESPONSE",
  GET_NEW_NOTIFICATIONS = "GET_NEW_NOTIFICATIONS",
  GET_NOTIFICATIONS_COUNT = "GET_NOTIFICATIONS_COUNT",
  READ_NOTIFICATIONS = "READ_NOTIFICATIONS",
  SET_NOTIFICATIONS_COUNT = "SET_NOTIFICATIONS_COUNT",
  SET_NOTIFICATION_READ = "SET_NOTIFICATION_READ",
  DISABLED_NOTIFICATIONS = "DISABLED_NOTIFICATIONS",
  SUBMIT_ERROR_REPORT="SUBMIT_ERROR_REPORT",
}

@Module
class NotificationModule extends BaseModule {
  notifications: Notification[] = []
  serverResponse: ServerResponse<Notification>  = new ServerResponse<Notification>()
  count = 0
  disabledNotifications = false


  get [`get/${NotificationActions.GET_NOTIFICATIONS_COUNT}`]() {
    return this.count
  }

  get [`get/${NotificationActions.GET_NOTIFICATIONS_RESPONSE}`]() {
    return this.serverResponse
  }

  get [`get/${NotificationActions.GET_NOTIFICATIONS}`]() {
    return this.notifications
  }

  get [`get/${NotificationActions.GET_NEW_NOTIFICATIONS}`]() {
    return this.notifications.filter(item => !item.read)
  }

  get [`get/${NotificationActions.DISABLED_NOTIFICATIONS}`]() {
    return userModule.selectedStep || this.disabledNotifications
  }

  @Mutation
  setNotifications(serverResponse: ServerResponse<Notification>) {
    const serverResponseContent = serverResponse.content.map((notification) => NotificationInit.create(notification))

    serverResponseContent.forEach((e, index) => {
      if (e.type === "ITEM_UPDATED") {
        serverResponseContent.splice(index, 1, ...NotificationInit.parseItemNotification(e))
      }
      if (e.type === "ITEM_SUB_TASK_ADDED_UPDATE") {
        serverResponseContent.splice(index, 1, ...NotificationInit.parseItemNotificationTasksUpdate(e))
      }
      if (e.type === "ITEM_CREATED") {
        serverResponseContent.splice(index, 1, ...NotificationInit.parseItemCreatedNotification(e))
      }
      if (e.type === "IDEA_ACTIVITY" && e instanceof NotificationIdeaActivity) {
        serverResponseContent.splice(index, 1, ...NotificationInit.parseIdeaActivityNotification(e))
      }
    })
    const content = parsingServerResponseContent({
      originalContent: this.serverResponse.content,
      serverResponseContent,
      numberOfPage: serverResponse.number,
    })

    this.serverResponse = { ...serverResponse, content }
    this.notifications = content
  }

  @Mutation
  setNotificationsCount(count: number) {
    this.count = count
  }
  
  @Mutation
  setDisabledNotification(value: boolean) {
    this.disabledNotifications = value
  }

  @Mutation
  markAsReadNotification() {
    this.notifications = this.notifications.map((item) => {
      if (!item.read) {
        return Object.assign(item, { read: true })
      } else {
        return item
      }
    })
  }

  @Action
  async [NotificationActions.GET_NOTIFICATIONS_RESPONSE](filterNotification: FilterNotification) {
    const filter = filterNotification.getJsonObj()
    const data = await apiService.get<NotificationResponse>(Endpoint.GET_NOTIFICATIONS(), filter)
    return this.handleResponse<NotificationResponse>(data, (data) => {
      this.setNotifications(data.notifications)
      this.setNotificationsCount(data.countOfUnreadNotificationTopics)
      return data
    })
  }

  @Action
  async [NotificationActions.READ_NOTIFICATIONS]() {
    const data = await apiService.post<any>(Endpoint.NOTIFICATIONS_MARK_AS_READ())
    this.markAsReadNotification()
    return this.handleResponse<number>(data, () => data)
  }

  @Action
  async [NotificationActions.SET_NOTIFICATIONS_COUNT](count: number) {
    this.setNotificationsCount(count)
  }

  @Action
  async [NotificationActions.DISABLED_NOTIFICATIONS](value: boolean) {
    this.setDisabledNotification(value)
  }

  @Action
  async [NotificationActions.SET_NOTIFICATION_READ](notificationsIds: string[]) {
    const count = await apiService.post<any>(Endpoint.NOTIFICATIONS_MARK_AS_READ(), notificationsIds)
    this.setNotificationsCount(count)
  }

  @Action
  async [NotificationActions.SUBMIT_ERROR_REPORT](alertReport: AlertReport) {
    const data = await apiService.post<any>(Endpoint.SUBMIT_ERROR_REPORT(), alertReport)

    return this.handleResponse<void>(data, async () => {
      const alertContent: AlertContentItem[] = [
        {
          text: "Error details successfully sent ",
          type: "bold",
        },
        {
          text: "to our support team.",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
  })}
}

export const notificationModule = new NotificationModule({
  store,
  name: Modules.NOTIFICATION,
})
